// src/pages/DeleteGuestEmailPage.jsx
// Reached via the "Delete my email now" link in booking status-change
// emails for guest (email-only) bookings — see mcp-email/handler.py::
// handle_booking_confirmation and mcp-booking/handler.py::
// get_guest_contact_by_token / delete_guest_contact.
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { TopBar } from "../components/UI";
import { api } from "../services/api";

export default function DeleteGuestEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [context, setContext] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Missing link — please use the link from your booking email.");
      setLoading(false);
      return;
    }
    api.getGuestContact(token)
      .then(setContext)
      .catch(e => setError(e.message || "This link is invalid or has already been used."))
      .finally(() => setLoading(false));
  }, [token]);

  const confirmDelete = async () => {
    setDeleting(true); setError("");
    try {
      await api.deleteGuestContact(token);
      setDone(true);
    } catch (e) {
      setError(e.message || "Failed to delete. The link may have already been used.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#F2F5EC" }}>
      <TopBar title="Wine4Collect" />
      <div style={{ maxWidth: 460, margin: "60px auto", padding: "0 16px" }}>
        <div style={{ background: "white", borderRadius: 12, padding: 32, border: "1px solid #C4D0A8" }}>
          {loading ? (
            <p style={{ fontSize: 13, color: "#6A7E58" }}>Loading…</p>
          ) : done ? (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>✓</div>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, color: "#3D4A1A", margin: "0 0 8px" }}>
                Your email has been deleted
              </h2>
              <p style={{ fontSize: 13, color: "#6A7E58" }}>
                It's been removed from our system. Your booking itself is unaffected.
              </p>
            </div>
          ) : error ? (
            <div style={{ textAlign: "center" }}>
              <p style={{ fontSize: 13, color: "#8B1A2F" }}>{error}</p>
            </div>
          ) : (
            <>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, color: "#1E2C14", margin: "0 0 8px" }}>
                Delete my email now?
              </h2>
              <p style={{ fontSize: 13, color: "#6A7E58", marginBottom: 20 }}>
                For your booking at <strong style={{ color: "#2C3820" }}>{context?.winery_name}</strong> on{" "}
                <strong style={{ color: "#2C3820" }}>{context?.visit_date}</strong> at{" "}
                <strong style={{ color: "#2C3820" }}>{context?.slot_time}</strong>.
              </p>

              <div style={{ background: "rgba(139,26,47,0.06)", border: "1px solid rgba(139,26,47,0.2)", borderRadius: 8, padding: "14px 16px", marginBottom: 20 }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: "#8B1A2F", margin: "0 0 8px" }}>
                  Before you confirm, please note:
                </p>
                <ul style={{ fontSize: 12, color: "#5A6E48", margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                  <li>If the winery needs to cancel or change your booking, they won't be able to reach you.</li>
                  <li>If a legal or contractual issue comes up about this visit, the winery has no way to contact you.</li>
                  <li>This can't be undone.</li>
                </ul>
              </div>

              <p style={{ fontSize: 12, color: "#6A7E58", marginBottom: 24 }}>
                Even if you do nothing, your email is automatically deleted within 24 hours of your visit —
                this link just lets you do it sooner. See our{" "}
                <Link to="/privacy" style={{ color: "#886070" }}>Privacy Policy</Link> for details.
              </p>

              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => navigate("/")} style={{
                  flex: 1, padding: "11px", background: "#E8EDE0", border: "1px solid #2A2A2A",
                  borderRadius: 8, color: "#5A6E48", fontSize: 13, cursor: "pointer",
                }}>
                  Keep my email
                </button>
                <button onClick={confirmDelete} disabled={deleting} style={{
                  flex: 1, padding: "11px", background: "#8B1A2F", border: "none",
                  borderRadius: 8, color: "white", fontWeight: 600, fontSize: 13,
                  cursor: "pointer", opacity: deleting ? 0.6 : 1,
                }}>
                  {deleting ? "Deleting…" : "Yes, delete it"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
